import { useAppNavigate } from "@base/model/hooks/useAppNavigate";
import { Button, Chip } from "@buildblockinc/buildblock-design-system";
import { ErrorBoundary } from "@sentry/react";
import { useParams } from "@tanstack/react-router";
import { Clock, Users } from "lucide-react";
import { Suspense } from "react";
import type { DailyLogSearchRouteParams } from "../model/dailyLogSearchState";
import { useProjectDetailQuery } from "../model/useProjectDetailQuery";

function ProjectOverviewTabContentSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="my-6 flex items-center gap-2 md:items-start md:gap-3 md:my-0 md:py-4">
        <div className="w-8 h-8 shrink-0 rounded-2 skeleton-shimmer" />
        <div className="flex flex-col gap-1.5">
          <div className="h-3 w-12 rounded-2 skeleton-shimmer" />
          <div className="h-4 w-48 rounded-2 skeleton-shimmer" />
        </div>
      </div>
      <div className="hidden gap-3 items-start py-4 border-t border-gray-100 md:flex">
        <div className="w-8 h-8 shrink-0 rounded-2 skeleton-shimmer" />
        <div className="flex flex-col gap-1.5">
          <div className="h-3 w-16 rounded-2 skeleton-shimmer" />
          <div className="h-4 w-24 rounded-2 skeleton-shimmer" />
        </div>
      </div>
      <div className="flex flex-col gap-2 md:py-4 md:border-t md:border-gray-100">
        <div className="h-4 w-16 rounded-2 skeleton-shimmer" />
        <div className="flex gap-2">
          <div className="h-8 w-24 rounded-circle skeleton-shimmer" />
          <div className="h-8 w-24 rounded-circle skeleton-shimmer" />
        </div>
      </div>
    </div>
  );
}

function ProjectOverviewTabContentError() {
  return <div className="py-10 text-center text-body3 text-gray-500">Failed to load project details.</div>;
}

function ProjectOverviewTabContentBody() {
  const { projectId } = useParams({ from: "/_authenticated/projects/$projectId/" });
  const { data: project } = useProjectDetailQuery(projectId);

  return (
    <>
      <div className="my-6 flex items-center gap-2 md:items-start md:gap-3 md:my-0 md:py-4">
        <LocationIcon />
        <div className="flex flex-col">
          <span className="text-label3 text-gray-500">Location</span>
          <span className="text-body4">{project.address}</span>
        </div>
      </div>

      <div className="hidden gap-3 items-start py-4 border-t border-gray-100 md:flex">
        <Clock width={32} height={32} strokeWidth={1.5} className="shrink-0 text-gray-400" aria-hidden="true" />
        <div className="flex flex-col">
          <span className="text-label3 text-gray-500">Timezone</span>
          <span className="text-body4">{project.timezone}</span>
        </div>
      </div>

      <div className="md:flex md:gap-3 md:items-start md:py-4 md:border-t md:border-gray-100">
        <Users
          width={32}
          height={32}
          strokeWidth={1.5}
          className="hidden shrink-0 text-gray-400 md:block"
          aria-hidden="true"
        />
        <div>
          <span className="text-body4">Members</span>
          <div className="flex flex-wrap gap-2 mt-2">
            {project.members.map((member) => (
              <Chip key={member.id} size="md" variant="fill" color="default">
                <span className="inline-block px-[10px]">{member.name}</span>
              </Chip>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function LocationIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      aria-hidden="true"
      className="shrink-0"
    >
      <rect width="32" height="32" fill="url(#pattern0_4557_7700)" />
      <defs>
        <pattern id="pattern0_4557_7700" patternContentUnits="objectBoundingBox" width="1" height="1">
          <use xlinkHref="#image0_4557_7700" transform="scale(0.0104167)" />
        </pattern>
        <image
          id="image0_4557_7700"
          width="96"
          height="96"
          preserveAspectRatio="none"
          xlinkHref="data:image/png;base64,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"
        />
      </defs>
    </svg>
  );
}

interface ProjectOverviewTabContentProps {
  /** Work History로 갔다가 Documents로 돌아올 때 유지할 검색 조건 (Work History 집계에는 쓰지 않는다) */
  documentsSearch: DailyLogSearchRouteParams;
}

export function ProjectOverviewTabContent({ documentsSearch }: ProjectOverviewTabContentProps) {
  const navigate = useAppNavigate();
  const { projectId } = useParams({ from: "/_authenticated/projects/$projectId/" });

  return (
    <>
      <div className="p-4 bg-white rounded-8 shadow-box">
        <h2 className="text-subtitle4 text-gray-1000 md:pb-3 md:-mx-4 md:px-4 md:border-b md:border-gray-100">
          Overview / Detail
        </h2>
        <ErrorBoundary fallback={<ProjectOverviewTabContentError />}>
          <Suspense fallback={<ProjectOverviewTabContentSkeleton />}>
            <ProjectOverviewTabContentBody />
          </Suspense>
        </ErrorBoundary>
      </div>
      <Button
        variant="outline"
        color="secondary"
        // PC는 프로젝트 탭에 Work History가 있어 모바일에서만 보여 준다.
        className="bg-white w-full mt-4 md:hidden"
        size="large"
        onClick={() =>
          navigate({
            to: "/projects/$projectId/history",
            params: {
              projectId,
            },
            state: { projectDocumentsSearch: documentsSearch },
          })
        }
      >
        Check work history
      </Button>
    </>
  );
}
